import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import {
  procedureColumnSchema,
  type ProcedureColumn,
} from "../models/audit/auditSession";

export interface ColumnVersionSnapshot {
  id: string;
  columnId: string;
  version: number;
  columnType: string;
  snapshot: ProcedureColumn;
  changedBy: string;
  createdAt: string;
}

const columnVersionSnapshotSchema = z.object({
  id: z.string(),
  columnId: z.string(),
  version: z.number(),
  columnType: z.string(),
  snapshot: procedureColumnSchema,
  changedBy: z.string(),
  createdAt: z.string(),
});

const columnHistoryResponseSchema = z.object({
  history: z.array(columnVersionSnapshotSchema),
  // The wrapper's live saved state — the baseline the revise wizard diffs its
  // draft against to surface unsaved in-session edits. Optional so older
  // server responses still parse.
  current: procedureColumnSchema.optional(),
});

export const useColumnHistory = (
  procedureId: string | null,
  columnId: string | null,
): {
  loading: boolean;
  history: ColumnVersionSnapshot[];
  savedColumn: ProcedureColumn | null;
  fetchHistory: () => Promise<void>;
} => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<ColumnVersionSnapshot[]>([]);
  const [savedColumn, setSavedColumn] = useState<ProcedureColumn | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!procedureId || !columnId) return;

    setLoading(true);
    try {
      const data = await fetchFastApi({
        path: `/matching/procedures/${procedureId}/columns/${columnId}/history`,
        method: "GET",
      });

      const parsed = columnHistoryResponseSchema.parse(data);
      setHistory(parsed.history);
      setSavedColumn(parsed.current ?? null);
    } catch (error) {
      console.error("Failed to fetch column history:", error);
      setHistory([]);
      setSavedColumn(null);
    } finally {
      setLoading(false);
    }
  }, [procedureId, columnId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    loading,
    history,
    savedColumn,
    fetchHistory,
  };
};
